import { Menu, Button, rem } from "@mantine/core"
import {
  IconCalendar,
  IconBrandOffice,
  IconBrandGoogle,
  IconBrandApple,
} from "@tabler/icons-react"
import { WebCalendarProfileDto } from "@/orval/gen"
import { useTranslation } from "react-i18next"
import { AddToGoogleCalendarModal } from "./add-to-google-calendar-modal"
import {
  getGetApiWebCalendarGetProfilesQueryKey,
  useCreateWebcalendarSubscription,
} from "@/orval/gen"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useDeviceType } from "@/-components/useDeviceType"
import { useIsSmallScreen } from "@/utils/useIsSmallScreen"

function openProfileAsExternal(
  calendar: WebCalendarProfileDto,
  app: "outlook" | "apple"
) {
  const isSupportingWebcals = app === "outlook"
  const url =
    isSupportingWebcals && calendar.webcalsLink && calendar.webcalsLink !== ""
      ? calendar.webcalsLink
      : calendar.webcalLink

  window.location.href = url ?? ""
}

interface Props {
  profile: WebCalendarProfileDto
}

export function SubscribeMenuButton({ profile }: Props) {
  const { t } = useTranslation("app_server")
  const isSmallScreen = useIsSmallScreen()
  const buttonProps = {
    size: isSmallScreen ? "sm" : "xs",
    mih: isSmallScreen ? 44 : undefined,
    h: isSmallScreen ? "auto" : undefined,
    maw: "100%",
    styles: { label: { whiteSpace: "normal" as const } },
  }
  const {
    isApple: isAppleDevice,
    isWindows: isWindowsDevice,
    isAndroid: isAndroidDevice,
  } = useDeviceType()
  const [googleCalendarModalOpen, setGoogleCalendarModalOpen] =
    React.useState(false)
  const client = useQueryClient()
  const { mutateAsync: subscribeToCalendar } = useCreateWebcalendarSubscription(
    {
      mutation: {
        onSuccess: () => {
          client.invalidateQueries({
            queryKey: getGetApiWebCalendarGetProfilesQueryKey(),
          })
        },
        onError: showErrorNotification,
      },
    }
  )
  const handleSubscribeToCalendar = async () => {
    await subscribeToCalendar({
      data: profile.webCalendarProfileId!,
    })
  }
  if (!profile.isSubsribed) {
    return (
      <Button
        leftSection={<IconCalendar size={14} />}
        variant="subtle"
        {...buttonProps}
        onClick={handleSubscribeToCalendar}
      >
        {t("Subscribe")}
      </Button>
    )
  }
  return (
    <>
      <Menu
        transitionProps={{ transition: "pop-top-right" }}
        width={220}
        withinPortal
      >
        <Menu.Target>
          <Button
            leftSection={<IconCalendar size={14} />}
            variant="subtle"
            {...buttonProps}
          >
            {t("OpenInCalendarApp")}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {isWindowsDevice && (
            <Menu.Item
              onClick={() => openProfileAsExternal(profile, "outlook")}
              leftSection={
                <IconBrandOffice
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Outlook {t("Calendar")}
            </Menu.Item>
          )}
          <Menu.Item
            onClick={() => setGoogleCalendarModalOpen(true)}
            leftSection={
              <IconBrandGoogle
                style={{
                  width: rem(16),
                  height: rem(16),
                }}
                stroke={1.5}
              />
            }
          >
            Google (Web) {t("Calendar")}
          </Menu.Item>
          {isAppleDevice && (
            <Menu.Item
              onClick={() => openProfileAsExternal(profile, "apple")}
              leftSection={
                <IconBrandApple
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Apple {t("Calendar")}
            </Menu.Item>
          )}
          {isAndroidDevice && (
            <Menu.Item
              onClick={() => {
                window.location.href = profile.webcalLink ?? ""
              }}
              leftSection={
                <IconCalendar
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Android {t("Calendar")}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <AddToGoogleCalendarModal
        opened={googleCalendarModalOpen}
        onClose={() => setGoogleCalendarModalOpen(false)}
        url={profile.externalLink ?? ""}
      />
    </>
  )
}
